"use client"

import React, { memo } from 'react'
import { useDraggable } from '@dnd-kit/core'

interface DraggableWorkspaceElementProps {
  id: string
  children: React.ReactNode
  disabled?: boolean
  cursorMode?: 'select' | 'pan'
  screenDeltaToSVGDelta?: (deltaX: number, deltaY: number) => { x: number, y: number }
  isInDragGroup?: boolean
  currentDelta?: { x: number, y: number }
}

export const DraggableWorkspaceElement = memo<DraggableWorkspaceElementProps>(({ 
  id, 
  children, 
  disabled = false,
  cursorMode = 'select',
  screenDeltaToSVGDelta,
  isInDragGroup = false,
  currentDelta = { x: 0, y: 0 }
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: disabled || cursorMode !== 'select'
  })

  // ✅ Filtrer les listeners pour ignorer les clics sur les poignées de resize
  const filteredListeners = React.useMemo(() => {
    if (!listeners) return {}
    
    return {
      ...listeners,
      onPointerDown: (event: React.PointerEvent) => {
        // Vérifier si le clic vient d'une poignée de resize
        const target = event.target as SVGElement
        if (target && (
          target.hasAttribute('data-resize-handle') ||
          target.closest('[data-resize-handle]')
        )) {
          // Ne pas activer le drag pour les poignées de resize
          return
        }
        
        // Sinon, activer le drag normalement
        if (listeners.onPointerDown) {
          listeners.onPointerDown(event as any)
        }
      }
    }
  }, [listeners])

  // Appliquer le delta du groupe pendant le déplacement
  const svgTransform = (isDragging || isInDragGroup) && (currentDelta.x !== 0 || currentDelta.y !== 0)
    ? `translate(${currentDelta.x}, ${currentDelta.y})`
    : undefined
  
  return (
    <g
      ref={setNodeRef}
      {...filteredListeners}
      {...attributes}
      data-element-id={id}
      className="workspace-element"
      transform={svgTransform}
      style={{
        opacity: isDragging ? 0.8 : isInDragGroup ? 0.8 : 1,
        cursor: cursorMode === 'select' && !disabled ? 'move' : 'default',
        pointerEvents: disabled ? 'none' : 'auto',
      } as any}
    >
      {children}
    </g>
  )
})

DraggableWorkspaceElement.displayName = 'DraggableWorkspaceElement'


